import { useEffect, useRef, useState } from 'react';

import { cn } from '@app/libs/utils';

interface TextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'placeholder' | 'onChange'> {
  label?: string;
  className?: string;
  autoResize?: boolean;
  content?: string;
  placeholder?: React.ReactNode | string;
  onChange?: (value: string) => void;
  errors?: string[];
  required?: boolean;
  description?: string;
}

const Textarea: React.FC<TextareaProps> = ({
  label,
  className,
  autoResize = false,
  content,
  maxLength,
  placeholder,
  onChange,
  errors = [],
  required,
  description,
  ...props
}) => {
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);
  const [value, setValue] = useState(content || '');
  const initRef = useRef<boolean>(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const recalculateHeight = () => {
    if (!textareaRef || !autoResize) return;

    // Kiểm tra xem element có đang visible không
    const rect = textareaRef.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      // Element đang bị ẩn, không tính toán
      return;
    }

    // Auto-resize
    textareaRef.style.height = 'auto';
    const scrollHeight = textareaRef.scrollHeight;
    if (scrollHeight > 0) {
      textareaRef.style.height = `${scrollHeight}px`;
    }
  };

  const handleInput = () => {
    if (!textareaRef?.value) return;

    // Enforce maxLength strictly (covers typing and paste)
    if (typeof maxLength === 'number' && maxLength > 0) {
      const current = textareaRef.value;
      if (current.length > maxLength) {
        textareaRef.value = current.slice(0, maxLength);
      }
    }

    // Auto-resize
    recalculateHeight();
    setValue(textareaRef.value);
  };

  useEffect(() => {
    if (!initRef.current) return;
    initRef.current = false;
    handleInput();
  }, [textareaRef]);

  useEffect(() => {
    // Initialize content length and size on mount/update
    if (textareaRef) {
      handleInput();
    }

    if (autoResize) {
      textareaRef?.addEventListener('input', handleInput);
    }

    return () => {
      if (autoResize) {
        textareaRef?.removeEventListener('input', handleInput);
      }
    };
  }, [autoResize, textareaRef, content, maxLength]);

  // Sử dụng ResizeObserver để detect khi parent element thay đổi kích thước
  useEffect(() => {
    if (!autoResize || !textareaRef || !containerRef.current) return;

    // Tìm parent element có thể bị collapse
    let parentElement: HTMLElement | null = textareaRef.parentElement;
    while (parentElement && parentElement !== document.body) {
      const style = window.getComputedStyle(parentElement);
      if (style.display === 'none' || style.visibility === 'hidden') {
        // Tìm parent element visible gần nhất
        parentElement = parentElement.parentElement;
        continue;
      }
      break;
    }

    const targetElement = parentElement || containerRef.current;

    // Tạo ResizeObserver để detect khi element hoặc parent thay đổi kích thước
    resizeObserverRef.current = new ResizeObserver(() => {
      // Sử dụng setTimeout để đảm bảo DOM đã được update
      setTimeout(() => {
        recalculateHeight();
      }, 0);
    });

    // Observe cả textarea và parent element
    resizeObserverRef.current.observe(textareaRef);
    if (targetElement !== textareaRef) {
      resizeObserverRef.current.observe(targetElement);
    }

    // Sử dụng MutationObserver để detect khi parent được hiển thị lại
    const mutationObserver = new MutationObserver(() => {
      setTimeout(() => {
        recalculateHeight();
      }, 0);
    });

    if (parentElement) {
      mutationObserver.observe(parentElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false,
        subtree: false,
      });
    }

    // Sử dụng IntersectionObserver để detect khi element được hiển thị lại
    const intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting || entry.intersectionRatio > 0) {
            setTimeout(() => {
              recalculateHeight();
            }, 0);
          }
        });
      },
      { threshold: 0.01 }
    );

    intersectionObserver.observe(textareaRef);

    return () => {
      resizeObserverRef.current?.disconnect();
      mutationObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [autoResize, textareaRef]);

  useEffect(() => {
    if (content !== undefined) {
      setValue(content);
      // Tính lại height khi content thay đổi
      if (textareaRef && autoResize) {
        setTimeout(() => {
          recalculateHeight();
        }, 0);
      }
    }
  }, [content, textareaRef, autoResize]);

  return (
    <div className="flex flex-col gap-2 relative" ref={containerRef}>
      <div className="flex justify-between flex-wrap gap-2">
        {label && (
          <label className="text-sm font-medium w-full md:w-auto">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        {maxLength && (
          <span className="text-xs text-blue-800 text-right">
            {textareaRef?.value?.length}/{maxLength} ký tự
          </span>
        )}
      </div>
      <div className="flex flex-col gap-2 relative">
        <textarea
          className={cn(
            'w-full p-2 border border-gray-200 rounded-md',
            'peer/textarea',
            autoResize && 'resize-none overflow-hidden',
            errors.length > 0 && 'ring-2 ring-red-300',
            className,
            !className?.includes('min-h-') && 'min-h-10'
          )}
          {...props}
          ref={setTextareaRef}
          onInput={handleInput}
          onChange={e => onChange?.(e.target.value)}
          defaultValue={content}
        ></textarea>

        {placeholder && value.length <= 0 && (
          <div
            className={cn(
              'absolute top-2 left-2 text-gray-400 text-sm pointer-events-none whitespace-pre-line leading-relaxed',
              'peer-focus:hidden peer-focus-within:hidden peer-hover:hidden',
              value.length > 0 && 'hidden'
            )}
          >
            {placeholder}
          </div>
        )}
      </div>
      {description && <span className="text-xs text-green-500">{description}</span>}
    </div>
  );
};

export default Textarea;
